'use client'

import {
  Bot,
  User,
  ArrowRight,
  ArrowDown,
  Heart,
  Zap,
  XCircle,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  DollarSign,
} from 'lucide-react'

/**
 * AI-First Customer Service Diagrams
 * Visual components for the automation trap blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

// 1. Emotional Value Routing Flow — shows how the 3 models route differently
export function EmotionalValueRoutingDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THREE ROUTING MODELS COMPARED
        </h3>
        <p className='text-sm text-muted-foreground'>
          How each model handles a first-time buyer asking about pricing
        </p>
      </div>

      <div className='space-y-4'>
        {/* AI-First */}
        <div className='p-4 rounded-xl bg-red-500/5 border border-red-500/20'>
          <div className='flex items-center gap-2 mb-3'>
            <XCircle className='w-4 h-4 text-red-500' />
            <span className='text-sm font-bold text-foreground'>
              AI-First with Escape Hatch
            </span>
          </div>
          <div className='flex items-center gap-2 flex-wrap'>
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted text-xs'>
              <User className='w-3 h-3' />
              <span>Customer</span>
            </div>
            <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-xs'>
              <Bot className='w-3 h-3 text-red-500' />
              <span>AI Gatekeeper</span>
            </div>
            <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-orange-500/10 border border-orange-500/30 text-xs'>
              <AlertTriangle className='w-3 h-3 text-orange-500' />
              <span>Frustrated</span>
            </div>
            <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted text-xs text-muted-foreground'>
              <span>Escape hatch?</span>
            </div>
            <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-500/10 text-xs font-medium text-red-600 dark:text-red-400'>
              <span>~30% abandon</span>
            </div>
          </div>
        </div>

        {/* Human-First */}
        <div className='p-4 rounded-xl bg-[#008dcc]/5 border border-[#008dcc]/20'>
          <div className='flex items-center gap-2 mb-3'>
            <CheckCircle className='w-4 h-4 text-[#008dcc]' />
            <span className='text-sm font-bold text-foreground'>
              Human-First + AI Copilot
            </span>
          </div>
          <div className='flex items-center gap-2 flex-wrap'>
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted text-xs'>
              <User className='w-3 h-3' />
              <span>Customer</span>
            </div>
            <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#008dcc]/10 border border-[#008dcc]/30 text-xs'>
              <User className='w-3 h-3 text-[#008dcc]' />
              <span>Human + AI</span>
            </div>
            <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-xs'>
              <Heart className='w-3 h-3 text-green-500' />
              <span>Trust built</span>
            </div>
            <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-500/10 text-xs font-medium text-green-600 dark:text-green-400'>
              <span>~70% convert</span>
            </div>
          </div>
        </div>

        {/* EV Hybrid */}
        <div className='p-4 rounded-xl bg-green-500/5 border border-green-500/20'>
          <div className='flex items-center gap-2 mb-3'>
            <Zap className='w-4 h-4 text-green-500' />
            <span className='text-sm font-bold text-foreground'>
              Emotional Value Routing (Hybrid)
            </span>
          </div>
          <div className='flex items-center gap-2 flex-wrap'>
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted text-xs'>
              <User className='w-3 h-3' />
              <span>Customer</span>
            </div>
            <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#FDC304]/10 border border-[#FDC304]/30 text-xs'>
              <Zap className='w-3 h-3 text-[#FDC304]' />
              <span>EV Score</span>
            </div>
            <ArrowDown className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex flex-col gap-1.5'>
              <div className='flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#008dcc]/10 text-[10px]'>
                <span>High EV → Human-led</span>
              </div>
              <div className='flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#8b5cf6]/10 text-[10px]'>
                <span>Med EV → AI Copilot</span>
              </div>
              <div className='flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-500/10 text-[10px]'>
                <span>Low EV → AI Self-serve</span>
              </div>
            </div>
            <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-500/10 text-xs font-medium text-green-600 dark:text-green-400'>
              <span>~75% convert</span>
            </div>
          </div>
        </div>
      </div>

      <p className='text-[10px] text-muted-foreground text-center mt-4'>
        Conversion ranges based on industry observations across hybrid routing
        implementations
      </p>
    </div>
  )
}

// 2. Industry Stats — Hard data validating the thesis
export function CustomerServiceStatsDiagram() {
  const stats = [
    {
      value: '64%',
      label: 'of customers prefer companies NOT use AI for service',
      source: 'Gartner, 2024',
      color: '#ef4444',
    },
    {
      value: '89%',
      label: 'favor hybrid: AI speed + human empathy',
      source: 'Industry survey',
      color: '#22c55e',
    },
    {
      value: '50%',
      label: 'of orgs will abandon AI workforce reduction plans',
      source: 'Gartner, 2025',
      color: '#FDC304',
    },
    {
      value: '20%',
      label: 'less time on routine cases with AI copilot',
      source: 'Salesforce, 2025',
      color: '#008dcc',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE DATA BACKS THE HYBRID MODEL
        </h3>
        <p className='text-sm text-muted-foreground'>
          Customers want AI speed with human trust — not one or the other
        </p>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        {stats.map((stat, idx) => (
          <div
            key={idx}
            className='p-4 rounded-xl border text-center'
            style={{
              backgroundColor: `${stat.color}08`,
              borderColor: `${stat.color}30`,
            }}
          >
            <p
              className='text-2xl md:text-3xl font-bold'
              style={{ color: stat.color }}
            >
              {stat.value}
            </p>
            <p className='text-[10px] text-muted-foreground mt-1 leading-tight'>
              {stat.label}
            </p>
            <p className='text-[9px] text-muted-foreground mt-1.5'>
              {stat.source}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

// 3. ROI Comparison Visual
export function HybridROIDiagram() {
  const models = [
    {
      name: 'AI-First',
      inquiries: 1000,
      abandonment: 30,
      conversionRate: 40,
      conversions: 280,
      revenue: '$560K',
      color: '#ef4444',
    },
    {
      name: 'Hybrid EV',
      inquiries: 1000,
      abandonment: 7,
      conversionRate: 75,
      conversions: 700,
      revenue: '$1.4M',
      color: '#22c55e',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          ILLUSTRATIVE ROI: 1,000 MONTHLY INQUIRIES
        </h3>
        <p className='text-sm text-muted-foreground'>
          Same traffic, different routing — $840K/month potential gap
        </p>
      </div>

      <div className='space-y-4'>
        {models.map((model, idx) => (
          <div
            key={idx}
            className='p-4 rounded-xl border'
            style={{
              backgroundColor: `${model.color}05`,
              borderColor: `${model.color}25`,
            }}
          >
            <div className='flex items-center justify-between mb-3'>
              <span className='text-sm font-bold text-foreground'>
                {model.name}
              </span>
              <span
                className='text-lg font-bold'
                style={{ color: model.color }}
              >
                {model.revenue}/mo
              </span>
            </div>
            <div className='grid grid-cols-3 gap-2'>
              <div className='text-center p-2 rounded-lg bg-muted/50'>
                <p className='text-xs text-muted-foreground'>Abandonment</p>
                <p className='text-sm font-bold' style={{ color: model.color }}>
                  ~{model.abandonment}%
                </p>
              </div>
              <div className='text-center p-2 rounded-lg bg-muted/50'>
                <p className='text-xs text-muted-foreground'>Conversion</p>
                <p className='text-sm font-bold' style={{ color: model.color }}>
                  ~{model.conversionRate}%
                </p>
              </div>
              <div className='text-center p-2 rounded-lg bg-muted/50'>
                <p className='text-xs text-muted-foreground'>Conversions</p>
                <p className='text-sm font-bold' style={{ color: model.color }}>
                  ~{model.conversions}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delta */}
      <div className='mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30'>
        <div className='flex items-center justify-center gap-3'>
          <TrendingUp className='w-5 h-5 text-green-500' />
          <div className='text-center'>
            <p className='text-lg font-bold text-green-500'>
              +$840K/mo potential
            </p>
            <p className='text-[10px] text-muted-foreground'>
              +$20K cost in human support → ~42x return on additional investment
            </p>
          </div>
          <DollarSign className='w-5 h-5 text-green-500' />
        </div>
      </div>

      <p className='text-[10px] text-muted-foreground text-center mt-3'>
        Illustrative scenario using $2K avg deal value. Actual results vary by
        industry and implementation.
      </p>
    </div>
  )
}
